import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { getPushState, enablePush, disablePush, sendTestPush } from "../../lib/push";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "sonner";
import { Bell, MessageSquare, UserPlus, Rocket, Inbox, CheckCircle2, Mail, Smartphone, Loader2 } from "lucide-react";

const KIND_ICONS = {
  support: MessageSquare, application: UserPlus, booking: Rocket,
  quote: Inbox, mission: CheckCircle2, contact: Mail,
};

const timeAgo = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [pushState, setPushState] = useState("idle");

  const togglePush = async () => {
    if (pushState === "unsupported") { toast.error("This browser doesn't support push notifications"); return; }
    if (pushState === "denied") { toast.error("Notifications are blocked — allow them in your browser/app settings"); return; }
    const prev = pushState;
    setPushState("working");
    try {
      if (prev === "enabled") {
        await disablePush();
        setPushState("idle");
        toast.success("Phone alerts off for this device");
      } else {
        await enablePush();
        setPushState("enabled");
        toast.success("Phone alerts armed — this device gets pinged on bookings & support requests");
      }
    } catch (e) {
      setPushState(await getPushState());
      toast.error(e?.message || "Couldn't enable push notifications");
    }
  };

  const fireTest = async () => {
    try {
      const sent = await sendTestPush();
      toast.success(sent > 0 ? `Test alert sent to ${sent} device${sent !== 1 ? "s" : ""}` : "No armed devices found");
    } catch {
      toast.error("Test failed — try re-enabling alerts");
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ops/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    try {
      await api.post("/ops/notifications/read-all");
      load();
    } catch {}
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) { load(); getPushState().then(setPushState); } }}>
      <PopoverTrigger asChild>
        <button data-testid="ops-notification-bell" aria-label="Notifications"
          className="relative p-2 rounded-full hover:bg-secondary transition-colors duration-200">
          <Bell className="w-[18px] h-[18px]" />
          {unread > 0 && (
            <span data-testid="ops-notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] max-w-[calc(100vw-24px)] p-0" data-testid="ops-notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <p className="font-heading font-semibold text-sm">Notifications</p>
          {unread > 0 && (
            <button data-testid="ops-notifications-mark-read" onClick={markAll}
              className="text-xs text-[var(--cc-accent)] font-semibold hover:opacity-80">
              Mark all read
            </button>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b bg-secondary/40">
          <button data-testid="ops-push-toggle" onClick={togglePush} disabled={pushState === "working"}
            className={`flex items-center gap-2 text-xs font-semibold rounded-full border px-3.5 py-1.5 transition-colors duration-200 ${
              pushState === "enabled" ? "text-emerald-500 border-emerald-500/50 bg-emerald-500/10"
                : "text-muted-foreground hover:text-foreground hover:border-[var(--cc-accent)]/60"}`}>
            {pushState === "working" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Smartphone className="w-3.5 h-3.5" />}
            {pushState === "enabled" ? "Phone alerts ON" : "Enable phone alerts"}
          </button>
          {pushState === "enabled" && (
            <button data-testid="ops-push-test" onClick={fireTest} className="text-xs text-[var(--cc-accent)] font-semibold hover:opacity-80">
              Send test
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-10">All quiet. Nothing new.</p>
          )}
          {items.map((n) => {
            const Icon = KIND_ICONS[n.kind] || Bell;
            return (
              <button key={n.id} data-testid={`ops-notification-item-${n.id}`}
                onClick={() => { if (n.link) { navigate(n.link); setOpen(false); } }}
                className={`w-full text-left flex gap-3 px-4 py-3 border-b last:border-0 hover:bg-secondary/60 transition-colors duration-150 ${!n.read ? "bg-[var(--cc-accent)]/5" : ""}`}>
                <span className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${!n.read ? "bg-[var(--cc-accent)]/15 text-[var(--cc-accent)]" : "bg-secondary text-muted-foreground"}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-semibold leading-snug">{n.title}</span>
                  {n.body && <span className="block text-xs text-muted-foreground mt-0.5 leading-snug line-clamp-2">{n.body}</span>}
                  <span className="block text-[10px] text-muted-foreground font-mono-cc mt-1 uppercase">{timeAgo(n.created_at)}</span>
                </span>
                {!n.read && <span className="w-2 h-2 rounded-full bg-[var(--cc-accent)] mt-2 shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
